import { ReferentialKey } from "api/referential";
import { SetDropdownItemsData, SetReferentialsData } from "../actions/types";

interface CachedReferentialData {
  referentials: SetReferentialsData;
  dropdownItems: SetDropdownItemsData;
  timestamp: number;
  version: string;
}

class ReferentialCacheService {
  private readonly CACHE_KEY = 'referentials_cache';
  private readonly CACHE_VERSION = '1.0';
  private readonly CACHE_DURATION = 365 * 24 * 60 * 60 * 1000;

  /**
   * Sauvegarde les données référentielles dans le localStorage
   */
  saveToCache(referentials: SetReferentialsData, dropdownItems: SetDropdownItemsData): void {
    try {
      const cacheData: CachedReferentialData = {
        referentials,
        dropdownItems,
        timestamp: Date.now(),
        version: this.CACHE_VERSION
      };

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Erreur lors de la sauvegarde du cache des référentiels:', error);
    }
  }

  /**
   * Récupère les données référentielles depuis le localStorage
   */
  getFromCache(): CachedReferentialData | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cacheData: CachedReferentialData = JSON.parse(cached);

      // Vérification de la version du cache
      if (cacheData.version !== this.CACHE_VERSION) {
        this.clearCache();
        return null;
      }

      // Vérification de l'expiration du cache
      if (Date.now() - cacheData.timestamp > this.CACHE_DURATION) {
        this.clearCache();
        return null;
      }

      return cacheData;
    } catch (error) {
      console.warn('Erreur lors de la récupération du cache des référentiels:', error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Sauvegarde un référentiel spécifique dans le cache
   */
  saveSingleReferentialToCache(
    referentialKey: ReferentialKey,
    referential: any[],
    dropdownItem: any[]
  ): void {
    const cachedData = this.getFromCache();
    if (!cachedData) {
      // Si pas de cache existant, créer un nouveau cache avec ce seul référentiel
      const newReferentials: SetReferentialsData = { [referentialKey]: referential };
      const newDropdownItems: SetDropdownItemsData = { [referentialKey]: dropdownItem };
      this.saveToCache(newReferentials, newDropdownItems);
      return;
    }

    // Mettre à jour le cache existant
    const updatedReferentials = { ...cachedData.referentials };
    const updatedDropdownItems = { ...cachedData.dropdownItems };

    updatedReferentials[referentialKey] = referential;
    updatedDropdownItems[referentialKey] = dropdownItem;

    this.saveToCache(updatedReferentials, updatedDropdownItems);
  }

  /**
   * Vérifie si un référentiel spécifique existe dans le cache
   */
  isReferentialCached(referentialKey: ReferentialKey): boolean {
    const cachedData = this.getFromCache();
    if (!cachedData) return false;

    const referential = cachedData.referentials[referentialKey];
    const dropdownItem = cachedData.dropdownItems[referentialKey];

    return !!(referential !== undefined &&
      dropdownItem !== undefined &&
      Array.isArray(referential) &&
      referential.length > 0);
  }

  /**
   * Récupère un référentiel spécifique depuis le cache
   */
  getSingleReferentialFromCache(referentialKey: ReferentialKey): {
    referential: any[];
    dropdownItem: any[];
  } | null {
    const cachedData = this.getFromCache();
    if (!cachedData) return null;

    const referential = cachedData.referentials[referentialKey];
    const dropdownItem = cachedData.dropdownItems[referentialKey];

    if (!referential || !dropdownItem ||
      !Array.isArray(referential) || !Array.isArray(dropdownItem)) {
      return null;
    }

    return { referential, dropdownItem };
  }

  /**
   * Vide le cache des référentiels
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.warn('Erreur lors de la suppression du cache des référentiels:', error);
    }
  }

  /**
   * Vérifie si le cache complet existe et est valide
   */
  isCacheValid(): boolean {
    const cachedData = this.getFromCache();
    return cachedData !== null;
  }

  /**
   * Obtient les statistiques du cache
   */
  getCacheStats(): {
    hasCache: boolean;
    cacheSize: number;
    cacheAge: number;
    version: string;
  } {
    const cachedData = this.getFromCache();

    return {
      hasCache: cachedData !== null,
      cacheSize: cachedData ? JSON.stringify(cachedData).length : 0,
      cacheAge: cachedData ? Date.now() - cachedData.timestamp : 0,
      version: cachedData?.version || 'N/A'
    };
  }
}

// Export d'une instance singleton
export const referentialCacheService = new ReferentialCacheService();
